"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function SproutDot({
  className = ""
}: {
  className?: string;
}) {
  return <span className={`relative inline-block w-1.5 h-1.5 rounded-lg bg-primary ${className}`} aria-hidden="true">
      <span className="absolute -top-1 left-1 w-1 h-1.5 border-t-[1.5px] border-r-[1.5px] border-primary rounded-tr-lg rotate-12" />
    </span>;
}
function SquiggleHighlight() {
  return <svg className="absolute left-0 right-0 -bottom-1 w-full h-2" viewBox="0 0 80 8" preserveAspectRatio="none" aria-hidden="true">
      <path d="M2 5 Q 12 1, 22 4 T 42 4 T 62 4 T 78 4" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" fill="none" />
    </svg>;
}
function DesktopNavConsumer({
  activeHash
}: {
  activeHash: string;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-6 w-16 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted/60 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => {
        const href = item.href || "#";
        const hash = href.includes("#") ? "#" + href.split("#")[1] : "";
        const isActive = hash && activeHash === hash;
        return <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={href} className="group relative inline-flex items-center gap-2 px-3 py-2 text-foreground hover:text-foreground hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent" style={{
              fontFamily: "var(--font-default)",
              fontSize: "0.9375rem",
              fontWeight: 500,
              letterSpacing: "0.01em"
            }}>
                  <span className={`inline-flex transition-all duration-200 ${isActive ? "opacity-100 scale-100" : "opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-100 group-hover:translate-x-1"}`}>
                    <SproutDot />
                  </span>
                  <span className="relative inline-block">
                    {item.label}
                    {isActive && <SquiggleHighlight />}
                  </span>
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 px-6">
        <div className="h-8 w-32 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-8 w-32 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-8 w-32 bg-muted/60 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} onClick={onNavigate} className="group flex items-center gap-3 py-3 text-foreground" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "1.375rem",
      fontWeight: 600,
      letterSpacing: "-0.01em"
    }}>
          <SproutDot className="transition-transform duration-200 group-hover:translate-x-1" />
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [activeHash, setActiveHash] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 24);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const sectionIds = ["menu", "nutrition", "story", "visit", "header"];
    const sections = sectionIds.map(id => document.getElementById(id)).filter((el): el is HTMLElement => el !== null);
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      if (visible[0]) {
        setActiveHash("#" + visible[0].target.id);
      }
    }, {
      rootMargin: "-30% 0px -60% 0px",
      threshold: [0, 0.25, 0.5, 0.75, 1]
    });
    sections.forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (hashPart === "") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1013" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-200 bg-background text-foreground ${scrolled ? "bg-background/90 backdrop-blur-md border-b border-border" : "bg-background"}`}>
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-5 md:py-6">
        <div className="grid grid-cols-12 items-center gap-4">
          {}
          <div className="col-span-8 md:col-span-9 flex items-center">
            <Link to="/HomePage" aria-label="Vanguard Shield Insurance home" className="inline-flex items-center">
              <WvcLogo className="h-10 md:h-11 w-auto" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex col-span-3 items-center justify-end gap-3">
            <MenuProvider menu_id="2">
              <DesktopNavConsumer activeHash={activeHash} />
            </MenuProvider>

            <Link to="/ClaimsCenterPage" className="inline-flex">
              <Button className="border-0 bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 h-auto font-default font-bold transition-all duration-200" style={{
              fontFamily: "var(--font-default)"
            }}>
                Get a Quote
              </Button>
            </Link>
          </div>

          {}
          <div className="col-span-4 md:hidden flex items-center justify-end">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="border border-border bg-transparent hover:bg-foreground hover:text-background h-11 w-11" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-l border-border w-[88%] sm:max-w-sm p-0 flex flex-col">
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
                  <SheetTitle className="text-left text-foreground" style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "0.8125rem",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontWeight: 500
                }}>
                    Navigation
                  </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto py-6">
                  <MenuProvider menu_id="2">
                    <MobileNavConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="px-6 pb-8 pt-4 border-t border-border">
                  <SheetClose asChild>
                    <Link to="/ClaimsCenterPage" className="block w-full">
                      <Button className="w-full border-0 bg-primary text-primary-foreground hover:bg-primary/90 py-3 h-auto font-default font-bold" style={{
                      fontFamily: "var(--font-default)"
                    }}>
                        Get a Quote
                      </Button>
                    </Link>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}